import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { ThemeProvider } from "@/components/ui/theme-provider";
import { AuthProvider } from "@/contexts/AuthContext";
import { AuthGuard } from "@/components/auth/AuthGuard";
import { AppShell } from "@/components/layout/AppShell";
import { CookieConsent } from "@/components/CookieConsent";
import { DynamicTypeProvider } from "@/components/accessibility/DynamicTypeProvider";
import { AccessibilityIndicator } from "@/components/ui/accessibility-indicator";
import { GlobalRouteAnnouncer } from "@/components/accessibility/ScreenReaderAnnouncer";
import { LocationSyncProvider } from "@/components/LocationSyncProvider";
import { GoogleMapsProvider } from "@/providers/GoogleMapsProvider";
import { RunSessionProvider } from "@/providers/RunSessionProvider";
import { ActiveRunPill } from "@/components/run/ActiveRunPill";
import { lazy, Suspense, useEffect } from "react";
import React from "react";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { useBackgroundLocation } from "@/hooks/useBackgroundLocation";
import { useAutoSync } from "@/hooks/useAutoSync";
import { App as CapacitorApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';
import { logger } from '@/lib/logger';
import { supabase } from '@/integrations/supabase/client';
import { SpotifyDeepLinkListener } from '@/components/oauth/SpotifyDeepLinkListener';
import { BLEDebugPanel } from '@/components/debug/BLEDebugPanel';
import { HealthDisclaimerModal } from '@/components/compliance/HealthDisclaimerModal';

// Páginas críticas carregadas imediatamente
import Login from "./pages/Login";
import { UnifiedSignupFlow } from "./components/auth/UnifiedSignupFlow";
import { MinimalSignupFlow } from "./components/auth/MinimalSignupFlow";
import { SignupMethodSelector } from "./components/auth/SignupMethodSelector";
import { SMSSignupFlow } from "./components/auth/SMSSignupFlow";
import { WhatsAppSignupFlow } from "./components/auth/WhatsAppSignupFlow";
import AuthCallback from "./pages/AuthCallback";
import ShareRun from "./pages/ShareRun";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import NotFound from "./pages/NotFound";
import SpotifyCallback from "./pages/auth/SpotifyCallback";
import MusicTest from "./pages/MusicTest";
import MusicAnalytics from "./pages/MusicAnalytics";
import MusicSocial from "./pages/MusicSocial";
import Run from "./pages/Run";
import GoogleCallback from "./pages/oauth/GoogleCallback";
import SpotifyOAuthCallback from "./pages/oauth/SpotifyCallback";
import SpotifyTest from "./pages/SpotifyTest";
import SpotifySetup from "./pages/SpotifySetup";
import MusicProviderTest from "./pages/MusicProviderTest";

// Lazy load de rotas não-críticas
const Index = lazy(() => import("./pages/Index"));
const CompleteProfile = lazy(() => import("./pages/CompleteProfile"));
const Community = lazy(() => import("./pages/Community"));
const Club = lazy(() => import("./pages/Club"));
const Profile = lazy(() => import("./pages/Profile"));
const Subscription = lazy(() => import("./pages/Subscription"));
const Admin = lazy(() => import("./pages/Admin"));
const ErrorLogs = lazy(() => import("./pages/admin/ErrorLogs"));
const InvestorRelations = lazy(() => import("./pages/InvestorRelations"));
const RunnerMatch = lazy(() => import("./pages/RunnerMatch"));
const MatchChatWindow = lazy(() => import("./components/match/MatchChatWindow"));
const MatchProfile = lazy(() => import("./pages/MatchProfile"));
const SocialFeed = lazy(() => import("./pages/SocialFeed"));
const Challenges = lazy(() => import("./pages/Challenges"));
const ChallengeRunner = lazy(() => import("./pages/ChallengeRunner"));
const ChallengesInbox = lazy(() => import("./pages/ChallengesInbox"));
const ChallengeDetails = lazy(() => import("./pages/ChallengeDetails"));
const ChallengeProgress = lazy(() => import("./pages/ChallengeProgress"));
const Leaderboards = lazy(() => import("./pages/Leaderboards"));
const AICoach = lazy(() => import("./pages/AICoach"));
const Training = lazy(() => import("./pages/Training"));
const ManageTrainingPlans = lazy(() => import("./pages/ManageTrainingPlans"));
const CreateTrainingPlanPage = lazy(() => import("./pages/CreateTrainingPlanPage"));
const TrainingPlanDetails = lazy(() => import("./pages/TrainingPlanDetails"));
const RunSummaryPage = lazy(() => import("./pages/RunSummaryPage"));
const Integrations = lazy(() => import("./pages/settings/Integrations"));
const EducationalContent = lazy(() => import("./pages/EducationalContent"));
const ArticleReader = lazy(() => import("./pages/ArticleReader"));
const LandingPage = lazy(() => import("./pages/LandingPage"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const AdminModeration = lazy(() => import("./pages/AdminModeration"));
const AdminAnalytics = lazy(() => import("./pages/AdminAnalytics"));
const NotificationSettings = lazy(() => import("./pages/NotificationSettings"));
const DebugDevice = lazy(() => import("./pages/DebugDevice"));
const DynamicTypeTest = lazy(() => import("./pages/DynamicTypeTest"));
const DebugMaps = lazy(() => import("./pages/DebugMaps"));
const EventDetails = lazy(() => import("./pages/EventDetails"));
const EventOrganizerDashboard = lazy(() => import("./pages/EventOrganizerDashboard"));
const Events = lazy(() => import("./pages/Events"));
const CreateEventPage = lazy(() => import("./pages/CreateEventPage"));
const EventEditPage = lazy(() => import("./pages/EventEditPage"));
const EventParticipantsPage = lazy(() => import("./pages/EventParticipantsPage"));
const EventDashboardPage = lazy(() => import("./pages/EventDashboardPage"));
const EventCheckInPage = lazy(() => import("./pages/EventCheckInPage"));
const EventLiveTrackingPage = lazy(() => import("./pages/EventLiveTrackingPage"));
const EventTrackingHistoryPage = lazy(() => import("./pages/EventTrackingHistoryPage").then(module => ({ default: module.default })));

// Páginas de configurações
const PersonalData = lazy(() => import("./pages/settings/PersonalData"));
const Address = lazy(() => import("./pages/settings/Address"));
const PrivacySettings = lazy(() => import("./pages/settings/Privacy"));
const NotificationsSettings = lazy(() => import("./pages/settings/Notifications"));
const BiometricSettings = lazy(() => import("./pages/settings/Biometric"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 2 * 60 * 1000, // 2 minutos por padrão
      gcTime: 5 * 60 * 1000, // 5 minutos (garbage collection)
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const DeepLinkHandler = () => {
  // Force dark theme on Android
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    
    const forceDarkTheme = async () => {
      try {
        // Status bar preta com ícones brancos
        await StatusBar.setStyle({ style: Style.Dark });
        await StatusBar.setBackgroundColor({ color: '#000000' });
        
        logger.info('Dark theme forced on native platform');
      } catch (error) {
        logger.error('Failed to force dark theme', error);
      }
    };
    
    forceDarkTheme();
  }, []);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    let listenerHandle: any = null;

    const setupListener = async () => {
      listenerHandle = await CapacitorApp.addListener('appUrlOpen', async (event: any) => {
        const correlationId = logger.getCorrelationId();
        logger.info('Deep link received', { url: event.url, correlationId });
        
        // ✅ CRITICAL FIX: Delegar callbacks do Spotify para SpotifyDeepLinkListener
        // Não processar aqui para evitar conflito de listeners
        if (event.url.includes('oauth/spotify/callback')) {
          logger.info('Spotify callback detected, delegating to SpotifyDeepLinkListener', { correlationId });
          return;
        }
        
        try {
          const url = new URL(event.url);
          
          // Validar domains permitidos (segurança FASE 1)
          const allowedHosts = [
            'youbetterrun.app',
            'www.youbetterrun.app',
            'b7dcf91f-8602-412b-8094-e7ac4397cf3f.lovableproject.com',
            'dnthpmvtdvipogrnekwg.supabase.co', // Supabase Auth
          ];
          
          const isValidDomain = allowedHosts.some(host => 
            url.hostname === host || url.hostname.endsWith(`.${host}`)
          );
          
          if (!isValidDomain && url.protocol !== 'youbetterrun:' && url.protocol !== 'com.youbetterrun.app:') {
            logger.warn('Deep link from untrusted domain blocked', { 
              hostname: url.hostname, 
              correlationId 
            });
            return;
          }
          
          // Supabase Auth Callback (OAuth, Magic Link, etc)
          if (url.pathname.includes('auth-callback') || url.hash.includes('access_token')) {
            logger.info('Processing Supabase auth callback', { correlationId });
            
            const hash = url.hash || url.href.split('#')[1];
            if (!hash) {
              logger.warn('No hash found in deep link');
              return;
            }

            const params = new URLSearchParams(hash.replace('#', ''));
            const accessToken = params.get('access_token');
            const refreshToken = params.get('refresh_token');
            const type = params.get('type');

            logger.info('Deep link auth tokens extracted', { 
              hasAccessToken: !!accessToken, 
              hasRefreshToken: !!refreshToken,
              type,
              correlationId
            });

            if (accessToken && refreshToken) {
              const { error } = await supabase.auth.setSession({
                access_token: accessToken,
                refresh_token: refreshToken,
              });

              if (error) {
                logger.error('Failed to set session from deep link', error, { correlationId });
                return;
              }

              logger.info('Session set successfully from deep link', { correlationId });
              
              // Verificar se perfil tem nome
              const { data: profile } = await supabase
                .from('profiles')
                .select('name')
                .eq('id', (await supabase.auth.getUser()).data.user?.id)
                .single();
              
              setTimeout(() => {
                if (type === 'recovery') {
                  window.location.href = '/reset-password';
                } else if (!profile?.name) {
                  window.location.href = '/complete-profile';
                } else {
                  window.location.href = '/dashboard';
                }
              }, 100);
            }
          }
          
          // Spotify OAuth Callback - já delegado no início da função
          // Este bloco não será mais executado devido ao early return acima
          
          // Outras deep link routes customizadas
          else if (url.pathname) {
            logger.info('Navigating to deep link path', { 
              path: url.pathname, 
              correlationId 
            });
            window.location.href = url.pathname;
          }
        } catch (error) {
          logger.error('Failed to parse deep link', error, { correlationId });
        }
      });
    };

    setupListener();

    return () => {
      if (listenerHandle) {
        listenerHandle.remove();
      }
    };
  }, []);

  return null;
};

const AnimatedRoutesWrapper = () => {
  const location = useLocation();
  
  // Hooks que dependem de AuthProvider devem estar aqui
  useBackgroundLocation();
  useAutoSync();
  
  // Limpar classe residual ybr-modal-open em cada mudança de rota
  useEffect(() => {
    if (document.body.classList.contains('ybr-modal-open')) {
      console.warn('[App] Found residual ybr-modal-open class on route change, removing');
      document.body.classList.remove('ybr-modal-open');
    }
  }, [location.pathname]);
  
  return (
    <>
      <DeepLinkHandler />
      <SpotifyDeepLinkListener />
      <HealthDisclaimerModal />
      <CookieConsent />
      <ActiveRunPill />
      <Suspense fallback={
        <div className="flex-1 flex items-center justify-center min-h-[60vh]">
          <LoadingSpinner size="lg" />
        </div>
      }>
          <Routes location={location}>
            {/* Rotas públicas */}
            <Route path="/landing" element={<LandingPage />} />
            <Route path="/share/:runId" element={<ShareRun />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup-method" element={<SignupMethodSelector />} />
            <Route path="/signup" element={<MinimalSignupFlow />} />
            <Route path="/signup-sms" element={<SMSSignupFlow />} />
            <Route path="/signup-whatsapp" element={<WhatsAppSignupFlow />} />
            <Route path="/auth-callback" element={<AuthCallback />} />
            <Route path="/complete-profile" element={
              <AuthGuard requireOnboarding={false}>
                <CompleteProfile />
              </AuthGuard>
            } />
            <Route path="/auth/spotify/callback" element={<SpotifyCallback />} />
            <Route path="/oauth/spotify/callback" element={<SpotifyOAuthCallback />} />
            <Route path="/spotify-test" element={<SpotifyTest />} />
            <Route path="/spotify-setup" element={<SpotifySetup />} />
            <Route path="/oauth/google/callback" element={<GoogleCallback />} />
            <Route path="/music-test" element={<MusicTest />} />
            <Route path="/music-analytics" element={<MusicAnalytics />} />
            <Route path="/music-social" element={<MusicSocial />} />
            <Route path="/music-provider-test" element={<MusicProviderTest />} />
            
            {/* Protected routes with persistent BottomNav */}
            <Route element={<AuthGuard><AppShell /></AuthGuard>}>
              <Route path="/" element={<Run />} />
              <Route path="/run" element={<Navigate to="/" replace />} />
              <Route path="/run/summary/:runId" element={<RunSummaryPage />} />
              <Route path="/history" element={<Navigate to="/dashboard" replace />} />
              <Route path="/dashboard" element={<Index />} />
              <Route path="/community" element={<Community />} />
              <Route path="/club" element={<Club />} />
              <Route path="/profile" element={<Profile />} />
              <Route path="/social" element={<SocialFeed />} />
              <Route path="/match" element={<RunnerMatch />} />
              <Route path="/match/chat/:matchId" element={<MatchChatWindow />} />
              <Route path="/match/profile/:matchId" element={<MatchProfile />} />
              <Route path="/challenges" element={<Challenges />} />
              <Route path="/challenge-runner" element={<ChallengeRunner />} />
              <Route path="/challenges-inbox" element={<ChallengesInbox />} />
              <Route path="/challenge/:id" element={<ChallengeDetails />} />
              <Route path="/challenge/:id/progress" element={<ChallengeProgress />} />
              <Route path="/leaderboards" element={<Leaderboards />} />
              <Route path="/coach" element={<AICoach />} />
              <Route path="/training" element={<Training />} />
              <Route
                path="/training/manage"
                element={
                  <AuthGuard>
                    <Suspense fallback={<LoadingSpinner />}>
                      <ManageTrainingPlans />
                    </Suspense>
                  </AuthGuard>
                }
              />
              <Route
                path="/training/create"
                element={
                  <AuthGuard>
                    <Suspense fallback={<LoadingSpinner />}>
                      <CreateTrainingPlanPage />
                    </Suspense>
                  </AuthGuard>
                }
              />
              <Route 
                path="/training/plan/:planId" 
                element={
                  <AuthGuard>
                    <Suspense fallback={<LoadingSpinner />}>
                      <TrainingPlanDetails />
                    </Suspense>
                  </AuthGuard>
                } 
              />
              <Route path="/training/:planId" element={<Training />} />
              <Route path="/subscription" element={<Subscription />} />
              <Route path="/admin-dashboard" element={<AdminDashboard />} />
              <Route path="/educational" element={<EducationalContent />} />
              <Route path="/educational/:slug" element={<ArticleReader />} />
              <Route path="/settings/personal-data" element={<PersonalData />} />
              <Route path="/settings/address" element={<Address />} />
              <Route path="/settings/privacy" element={<PrivacySettings />} />
              <Route path="/settings/notifications" element={<NotificationsSettings />} />
              <Route path="/settings/biometric" element={<BiometricSettings />} />
              <Route path="/settings/integrations" element={<Integrations />} />
              <Route path="/debug-device" element={<DebugDevice />} />
              <Route path="/dynamic-type-test" element={<DynamicTypeTest />} />
              <Route path="/debug/maps" element={<DebugMaps />} />
              <Route path="/events" element={<Events />} />
              <Route path="/events/create" element={<CreateEventPage />} />
              <Route path="/events/:id" element={<EventDetails />} />
              <Route path="/events/:id/edit" element={<EventEditPage />} />
              <Route path="/events/:id/participants" element={<EventParticipantsPage />} />
              <Route path="/events/:id/dashboard" element={<EventDashboardPage />} />
              <Route path="/events/:id/checkin" element={<EventCheckInPage />} />
            <Route path="/events/:id/live-tracking" element={<EventLiveTrackingPage />} />
            <Route path="/events/:id/tracking-history" element={<EventTrackingHistoryPage />} />
              <Route path="/events/:id/organizer" element={<EventOrganizerDashboard />} />
            <Route path="/share/event/:eventId" element={
              <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><LoadingSpinner size="lg" /></div>}>
                {React.createElement(lazy(() => import("./pages/ShareEvent")))}
              </Suspense>
            } />
            
            {/* Test pages for visual regression */}
            <Route path="/test/music-components" element={
              <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><LoadingSpinner size="lg" /></div>}>
                {React.createElement(lazy(() => import("./pages/test/MusicComponentsTest")))}
              </Suspense>
            } />
            </Route>
            
            {/* Public pages */}
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            {/* Redirects para Profile tabs */}
            <Route path="/achievements" element={<Navigate to="/profile?tab=conquistas" replace />} />
            <Route path="/performance" element={<Navigate to="/profile?tab=progresso" replace />} />
            <Route path="/analytics" element={<Navigate to="/profile?tab=progresso" replace />} />
            <Route path="/posture-dashboard" element={<Navigate to="/profile?tab=postura" replace />} />
            
            {/* Admin routes without BottomNav */}
            <Route path="/admin" element={
              <AuthGuard requiredRole="admin">
                <Admin />
              </AuthGuard>
            } />
            
            {/* Investor Relations */}
            <Route path="/investor-relations" element={
              <AuthGuard requiredRole="admin">
                <InvestorRelations />
              </AuthGuard>
            } />
            
            {/* Admin Moderation */}
            <Route path="/admin/moderation" element={
              <AuthGuard requiredRole="admin">
                <AdminModeration />
              </AuthGuard>
            } />
            
            {/* Admin Analytics */}
            <Route path="/admin/analytics" element={
              <AuthGuard requiredRole="admin">
                <AdminAnalytics />
              </AuthGuard>
            } />
            
            {/* Admin Error Logs */}
            <Route path="/admin/error-logs" element={
              <AuthGuard requiredRole="admin">
                <ErrorLogs />
              </AuthGuard>
            } />
            
            {/* Notification Settings */}
            <Route path="/settings/notifications-prefs" element={
              <AuthGuard>
                <NotificationSettings />
              </AuthGuard>
            } />
            
            <Route path="*" element={<NotFound />} />
          </Routes>
      </Suspense>
    </>
  );
};

const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <RunSessionProvider>
          <LocationSyncProvider>
            <GoogleMapsProvider>
              <ThemeProvider defaultTheme="dark-premium" storageKey="ybr-theme-preference">
                <DynamicTypeProvider>
                  <TooltipProvider>
                    <Toaster />
                    <Sonner />
                    <AccessibilityIndicator />
                    <GlobalRouteAnnouncer />
                    <BLEDebugPanel />
                    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
                      <AnimatedRoutesWrapper />
                    </BrowserRouter>
                  </TooltipProvider>
                </DynamicTypeProvider>
              </ThemeProvider>
            </GoogleMapsProvider>
          </LocationSyncProvider>
        </RunSessionProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
};

export default App;
